import React, { useState } from 'react';
import { useNoteAttachmentUrl } from '../hooks/useNoteAttachmentUrl';
import ConfirmDialog from './ConfirmDialog';
import Button from './ui/Button';

interface NoteImageModalProps {
  noteId: number;
  imageIds: number[];
  initialIndex?: number;
  alt: string;
  onClose: () => void;
}

/** Full-size attachment viewer. The shared dialog owns Escape and return focus. */
const NoteImageModal: React.FC<NoteImageModalProps> = ({
  noteId,
  imageIds,
  initialIndex = 0,
  alt,
  onClose,
}) => {
  const [index, setIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(imageIds.length - 1, 0)),
  );
  const imageId = imageIds[index] ?? null;
  const { url, loading, error, refetch } = useNoteAttachmentUrl(noteId, imageId);
  const [retriedIds, setRetriedIds] = useState<Set<number>>(new Set());

  const previous = () => setIndex((current) => Math.max(current - 1, 0));
  const next = () => setIndex((current) => Math.min(current + 1, imageIds.length - 1));

  return (
    <ConfirmDialog
      open
      title="Note attachment"
      cancelLabel="Close"
      confirmLabel={null}
      maxWidth="5xl"
      onConfirm={onClose}
      onCancel={onClose}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft' && index > 0) previous();
        if (event.key === 'ArrowRight' && index < imageIds.length - 1) next();
      }}
    >
      <div className="relative flex min-h-48 items-center justify-center">
        {loading && !url && <p role="status">Loading image…</p>}
        {error && !url && <p role="alert" className="text-destructive">{error}</p>}
        {url && (
          <img
            src={url}
            alt={alt}
            className="max-h-[65vh] max-w-full object-contain"
            onError={() => {
              if (imageId == null || retriedIds.has(imageId)) return;
              setRetriedIds((previousIds) => new Set(previousIds).add(imageId));
              refetch();
            }}
          />
        )}
        {imageIds.length > 1 && (
          <div className="absolute inset-x-2 bottom-2 flex items-center justify-between gap-2">
            <Button variant="secondary" onClick={previous} disabled={index === 0} title="Show the previous photo on this note">Previous image</Button>
            <span className="rounded bg-background/90 px-2 py-1 text-sm">{index + 1} / {imageIds.length}</span>
            <Button variant="secondary" onClick={next} disabled={index === imageIds.length - 1} title="Show the next photo on this note">Next image</Button>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
};

export default NoteImageModal;
